import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { deleteUser, listUsers, viewUser } from "../redux/slices/authSlice";
import { RootState, AppDispatch } from "../redux/store";
import {
  Typography,
  List,
  ListItemText,
  Button,
  Box,
  ListItemButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Switch,
  ListItem,
} from "@mui/material";
import Toast from "../components/Toast";
import useDetectSession from "../hooks/useDetectSession.hook";
import {
  pageHeading,
  primaryBtnStyle,
  primaryBtnStyleOutline,
} from "../styles/styling";
import AppContainer from "../components/AppContainer";
import { PermissionsByDocKey } from "../interfaces/permissions-interface";
import { transformArrToObj } from "../utils/transform-permissions-obj";
import LoadingOverlay from "../components/LoadingOverlay";

const DashboardPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();

  const [deleteModalOpen, setDeleteModalOpen] = useState<boolean>(false);
  const [deletePromptTitle, setDeletePromptTitle] = useState<string>("");
  const [deletePromptMessage, setDeletePromptMessage] = useState<string>("");
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [toastOpen, setToastOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [toastSeverity, setToastSeverity] = useState<"success" | "error">(
    "success"
  );

  useDetectSession();

  const { users, status, error } = useSelector(
    (state: RootState) => state.auth
  );

  const idToken = localStorage.getItem("idToken");
  const currentUser = useSelector((state: RootState) => state.auth.currentUser);

  const [currentUserPermissions, setCurrentUserPermissions] =
    useState<PermissionsByDocKey>();

  useEffect(() => {
    if (!idToken) {
      navigate("/login");
      return;
    }

    dispatch(listUsers());
  }, [dispatch, idToken, navigate]);

  useEffect(() => {
    if (currentUser && currentUser.userPermissions) {
      setCurrentUserPermissions(transformArrToObj(currentUser.userPermissions));
    }
  }, [currentUser]);

  if (error) {
    return (
      <AppContainer>
        <Typography variant="h6" color="error">
          Error: {error}
        </Typography>
      </AppContainer>
    );
  }

  const handleCreateUserClick = () => {
    navigate("/create-user");
  };
  const handleUserClick = async (id: string) => {
    await dispatch(viewUser({ id })).unwrap();
    navigate("/view-user", {
      state: { userId: id },
    });
  };

  const handleDeleteIconClick = (id: string, active: boolean) => {
    if (active) {
      setDeletePromptTitle("Deactivate User");
      setDeletePromptMessage("Are you sure you want to deactivate this user?");
    } else {
      setDeletePromptTitle("Activate User");
      setDeletePromptMessage("Are you sure you want to activate this user?");
    }
    setSelectedUserId(id);
    setDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (selectedUserId) {
      dispatch(
        deleteUser({
          id: selectedUserId,
          deletedBy: currentUser?.fullname ?? "",
        })
      )
        .unwrap()
        .then(() => {
          setToastMessage("User status updated successfully");
          setToastSeverity("success");
          setToastOpen(true);
          setDeleteModalOpen(false); // Close modal after deletion
          return dispatch(listUsers()).unwrap();
        })
        .catch(() => {
          setToastMessage("Failed to update user status");
          setToastSeverity("error");
          setToastOpen(true);
        });
    }
  };

  const handleDeleteCancel = () => {
    setDeleteModalOpen(false);
  };

  const handleToastClose = () => {
    setToastOpen(false);
  };

  return (
    <AppContainer>
      <Box display="flex" flexDirection="row" alignItems="center" mb={2}>
        <Typography variant="h4" sx={pageHeading} gutterBottom>
          Users
        </Typography>
        <Box flexGrow={1} />
        {currentUserPermissions?.createUser?.permissions?.edit && (
          <Button
            variant="contained"
            onClick={handleCreateUserClick}
            sx={primaryBtnStyle}
          >
            Create User
          </Button>
        )}
      </Box>
      {users?.length === 0 ? (
        <Typography>No users found</Typography>
      ) : (
        <List>
          {users?.map((user) => (
            <ListItem>
              <ListItemButton
                key={user.id}
                onClick={() => handleUserClick(user.id)}
                component={Box}
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mb={1}
              >
                <ListItemText
                  primary={`Name: ${user.fullName || "-"}`}
                  secondary={
                    <>
                      <Typography variant="body2">
                        Email: {user.email || "-"}
                      </Typography>
                      <Typography variant="body2">
                        Role: {user.role ?? "-"}
                      </Typography>
                      <Typography variant="body2">
                        Created by: {user.createdBy ?? "-"}
                      </Typography>
                      <Typography variant="body2">
                        Date Created:{" "}
                        {new Date(
                          user?.createdAt._seconds * 1000
                        ).toLocaleString()}
                      </Typography>
                      <Typography variant="body2">
                        User Status: {user?.active ? "Active" : "Deactivated"}
                      </Typography>
                    </>
                  }
                />
              </ListItemButton>
              <Switch
                disabled={
                  currentUserPermissions?.user?.permissions.delete !== true
                }
                defaultChecked={user.active}
                checked={user.active}
                onChange={(_e) => handleDeleteIconClick(user.id, user.active)}
              />
            </ListItem>
          ))}
        </List>
      )}

      <LoadingOverlay loading={status === "loading"} />
      {/* Confirmation Modal */}
      <Dialog
        open={deleteModalOpen}
        onClose={handleDeleteCancel}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">{deletePromptTitle}</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            {deletePromptMessage}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleDeleteCancel}
            variant="outlined"
            sx={primaryBtnStyleOutline}
          >
            Cancel
          </Button>
          <Button onClick={handleDeleteConfirm} autoFocus sx={primaryBtnStyle}>
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      {/* Toast Notification */}
      <Toast
        message={toastMessage}
        severity={toastSeverity}
        open={toastOpen}
        onClose={handleToastClose}
      />
    </AppContainer>
  );
};

export default DashboardPage;
